// Reusable comment thread + reply box. Renders public comments (and, when
// `showInternal`, internal team notes styled distinctly). Used by the public
// portal and the agent detail view.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { brand } from "../../../config/theme";
import { formatDate, type Comment, type Attachment } from "../services/serviceDeskService";

function AttachmentList({ attachments }: { attachments?: Attachment[] }) {
  if (!attachments || attachments.length === 0) return null;

  const isImage = (contentType?: string | null) => {
    return contentType?.startsWith("image/");
  };

  const isVideo = (contentType?: string | null) => {
    return contentType?.startsWith("video/");
  };

  const images = attachments.filter(a => isImage(a.contentType));
  const videos = attachments.filter(a => isVideo(a.contentType));
  const files = attachments.filter(a => !isImage(a.contentType) && !isVideo(a.contentType));

  return (
    <div className="mt-2 space-y-3">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {images.map((a, i) => (
            <div key={i} className="inline-block">
              <img
                src={a.url}
                alt={a.name}
                className={`max-h-64 max-w-sm rounded-lg border border-line shadow-md`}
              />
              <p className={`mt-1 text-xs text-content-muted`}>{a.name}</p>
            </div>
          ))}
        </div>
      )}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {videos.map((a, i) => (
            <div key={i} className="inline-block">
              <video
                src={a.url}
                controls
                className={`max-h-64 max-w-sm rounded-lg border border-line shadow-md bg-black`}
              />
              <p className={`mt-1 text-xs text-content-muted`}>{a.name}</p>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((a, i) => (
            <a
              key={i}
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors border-line bg-app text-content-secondary hover:bg-subtle"
            >
              <i className="ri-attachment-2" /> {a.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommentThread({
  comments,
  onReply,
  replyLabel = "Escribe un comentario…",
  showInternal = false,
  allowInternalToggle = false,
  internalOnly = false,
  emptyLabel,
}: {
  comments: Comment[];
  onReply?: (body: string, files: File[], internal: boolean) => Promise<void>;
  replyLabel?: string;
  showInternal?: boolean;
  allowInternalToggle?: boolean;
  /**
   * Render ONLY internal team notes and force every reply to be an internal
   * note (no public/internal toggle). Used by the dedicated "Notas internas"
   * section so internal notes live apart from the public client thread (#34).
   */
  internalOnly?: boolean;
  emptyLabel?: string;
}) {
  const { t } = useTranslation("serviceDesk");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [internal, setInternal] = useState(internalOnly);
  const [sending, setSending] = useState(false);

  // internalOnly → just the team notes; otherwise honor showInternal for
  // whether internal notes are interleaved with the public thread.
  const visible = comments.filter((c) =>
    internalOnly ? c.internal : showInternal || !c.internal,
  );

  // A reply is internal when the thread is internal-only or the agent ticked
  // the toggle.
  const isInternalReply = internalOnly || internal;

  const send = async () => {
    if (!onReply || (!body.trim() && files.length === 0)) return;
    setSending(true);
    try {
      await onReply(body.trim(), files, isInternalReply);
      setBody("");
      setFiles([]);
      setInternal(internalOnly);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="space-y-3">
        {visible.length === 0 && (
          <p className={`text-sm text-content-faint`}>
            {emptyLabel || (internalOnly ? t("ticketDetail.comments.noInternalNotes") : t("ticketDetail.comments.noComments"))}
          </p>
        )}
        {visible.map((c) => {
          // La IA tiene su propio authorType (#508): se ve distinta de una
          // persona del equipo tanto para el cliente como para el agente, para
          // que nadie crea que le contestó un humano.
          const isAi = c.authorType === "ai_agent";
          const isAgent = c.authorType === "agent" || isAi;
          const isInternal = Boolean(c.internal);

          let bgClass = "";
          let borderClass = "";

          if (isInternal) {
            bgClass = "bg-warning/10";
            borderClass = "border-warning/30";
          } else if (isAi) {
            bgClass = "bg-violet-50/50";
            borderClass = "border-violet-200";
          } else if (isAgent) {
            bgClass = "bg-subtle";
            borderClass = "border-line";
          } else {
            bgClass = "bg-info/10";
            borderClass = "border-info/30";
          }

          return (
            <div
              key={c.id}
              className={`rounded-lg border p-4 transition-colors ${bgClass} ${borderClass}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-content">
                  {isAi && <i className="ri-sparkling-2-line mr-1 text-violet-500" />}
                  {c.authorName}
                  {isAi ? (
                    <span className="ml-1 font-medium text-violet-600">· {t("ticketDetail.comments.aiReply")}</span>
                  ) : (
                    isAgent && <span className="ml-1 text-content-muted">· {t("ticketDetail.comments.agent")}</span>
                  )}
                  {isInternal && <span className="ml-1 font-medium text-warning">· {t("ticketDetail.comments.internalNote")}</span>}
                </span>
                <span className="text-[11px] text-content-muted">{formatDate(c.createdAt)}</span>
              </div>
              {c.body && <p className="whitespace-pre-wrap text-sm text-content-secondary">{c.body}</p>}
              <AttachmentList attachments={c.attachments} />
            </div>
          );
        })}
      </div>

      {onReply && (
        <div className={`mt-4 border-t border-line pt-4`}>
          <textarea
            className="w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 border-line bg-app text-content focus:ring-line"
            rows={3}
            placeholder={replyLabel}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <input
                type="file"
                multiple
                className={`text-xs text-content-muted`}
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
              {allowInternalToggle && !internalOnly && (
                <label className={`flex items-center gap-2 text-xs font-medium cursor-pointer text-warning`}>
                  <input
                    type="checkbox"
                    checked={internal}
                    onChange={(e) => setInternal(e.target.checked)}
                    className="cursor-pointer"
                  />
                  {t("ticketDetail.comments.internalToggle")}
                </label>
              )}
            </div>
            <button
              onClick={send}
              disabled={sending}
              className="rounded-full px-6 py-2 text-sm font-semibold text-white transition-opacity disabled:opacity-50 hover:shadow-lg"
              style={{ backgroundColor: isInternalReply ? "#b45309" : brand.primary }}
            >
              {sending ? t("ticketDetail.comments.sending") : isInternalReply ? t("ticketDetail.comments.addInternalNote") : t("ticketDetail.comments.send")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
